import type { DeleteAssessmentActivitiesMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { DeleteAssessmentActivitiesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export const useDeleteAssessmentActivities = createMutationHook({
  name: 'deleteAssessmentActivities',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.assessment.deleteActivities.mutationOptions({ onSuccess }),
  mapInput: (ids: string[]) => ({ Ids: ids }),
  invalidate: (trpc) => [
    trpc.frontend.assessment.activityRegister.queryKey(),
    trpc.frontend.assessment.activityById.queryKey(),
    trpc.frontend.assessment.assessmentActivitiesByParentId.queryKey(),
    trpc.frontend.assessment.assessmentRCSAActivitiesByAssessmentId.queryKey(),
    trpc.frontend.assessment.oversightActivityRegister.queryKey(),
  ],
  // The data-layer throws NotFound when 0 rows are deleted, so a successful
  // response guarantees all requested IDs were deleted.
  mapResponse: (
    _result,
    ids: string[]
  ): DeleteAssessmentActivitiesMutation => ({
    delete_assessment_activity: {
      __typename: 'assessment_activity_mutation_response',
      affected_rows: ids.length,
    },
  }),
  graphqlDocument: DeleteAssessmentActivitiesDocument,
  graphqlVariables: (ids: string[]) => ({ Ids: ids }),
  // Activity lists still read through Apollo when the flag is on, and the
  // factory evicts these on BOTH the Apollo and tRPC paths (ENG-616) so a tRPC
  // delete cannot leave a cache-first Apollo read showing pre-delete data.
  //
  // Eviction alone is NOT enough for every activity read. The register hooks go
  // through `createQueryHook`, whose default `graphqlFetchPolicy` is
  // `no-cache` — such a query is never stored in or watched through the
  // normalised cache, so evicting `ROOT_QUERY` cannot make it re-read. Those
  // consumers must call the hook's own `refetch()` after a delete; see
  // `pages/assessments/activities/Page.tsx` and the oversight register
  // (COI-820).
  evictFields: ['assessment_activity', 'assessment_activity_aggregate'],
});
